import {
  useWSModal,
  WSButton,
  WSButtons,
  WSElement,
  WSElementProps,
  WSForm,
  WSInputMask,
  WSMessageBox,
  WSText
} from "@wingspanhq/fe-component-library";
import React from "react";
import * as Yup from "yup";
import { useUserId } from "../../../../query/hooks/helpers";
import { useMutationUpdateMemberProfile } from "../../../../query/users/mutations";
import { getSsnInputProps } from "../../../../shared/utils/getSsnInputProps";
import { validatorSSN } from "../../../../utils/validators";
import { WS_LINKS } from "../../../../types/wsLinks";

type FormData = {
  ssn: string;
};

const ModalAddSSN: React.FC<
  { onClose: (result: boolean) => void } & WSElementProps
> = ({ onClose, ...elementProps }) => {
  const memberId = useUserId();
  const [updateMemberProfile, updateMemberProfileMeta] =
    useMutationUpdateMemberProfile();

  return (
    <WSElement {...elementProps}>
      <WSText.Heading5 mb="M">
        Add social security number or ITIN to send payment.
      </WSText.Heading5>
      <WSText my="XL">
        The IRS requires your full SSN or ITIN to receive your payment.
      </WSText>

      <WSForm<FormData>
        defaultValues={{ ssn: "" }}
        validationSchema={Yup.object().shape({
          ssn: validatorSSN.required("Required")
        })}
        onSubmit={async formData => {
          await updateMemberProfile({
            memberId,
            profile: { ssn: formData.ssn }
          });
          onClose(true);
        }}
      >
        <WSForm.Field
          label="SSN or ITIN"
          name="ssn"
          component={WSInputMask}
          componentProps={getSsnInputProps()}
          mb="2XL"
        />

        {updateMemberProfileMeta.isError ? (
          <WSMessageBox.Error>Something went wrong</WSMessageBox.Error>
        ) : null}

        <WSButtons format="modal">
          <WSButton.Secondary
            size="M"
            type="button"
            onClick={() => onClose(false)}
          >
            Cancel
          </WSButton.Secondary>
          <WSButton.Primary
            size="M"
            type="submit"
            loading={updateMemberProfileMeta.isLoading}
          >
            Submit SSN or ITIN
          </WSButton.Primary>
        </WSButtons>
      </WSForm>

      <WSText.ParagraphSm mt="XL">
        Wingspan uses bank-grade security to protect your data.{" "}
        <a href={WS_LINKS.security} target="_blank" rel="noreferrer">
          Learn more.
        </a>
      </WSText.ParagraphSm>
    </WSElement>
  );
};

export const useModalAddSSN = () =>
  useWSModal(ModalAddSSN, {
    title: "Add SSN or ITIN",
    size: "S"
  });
